/**
 * Member Households Tab - shows all DCM households this member belongs to,
 * with their role, individual needs count, services, and upcoming follow-ups.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { apiClient } from '@/services/apiClient';
import { MembershipRoleFields } from '@/components/households/MembershipRoleFields';
import HouseholdCustomFields from '@/components/households/HouseholdCustomFields';
import {
  Home,
  Users,
  AlertTriangle,
  Calendar,
  MapPin,
  ExternalLink,
  Loader2,
  ClipboardList,
  Clock,
  Phone,
  Mail,
  Plus,
  X,
  Search,
} from 'lucide-react';

interface MemberHousehold {
  household_member_id: number;
  household_id: number;
  role_in_household: string;
  relationship_to_head?: string;
  is_displaced: boolean;
  member_status: string;
  joined_date?: string;
  left_date?: string;
  current_address_line1?: string;
  current_city?: string;
  current_state?: string;
  current_zip?: string;
  current_phone?: string;
  current_email?: string;
  household_number: string;
  address_line1?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  disaster_event?: string;
  disaster_date?: string;
  displacement_status?: string;
  property_type?: string;
  household_status: string;
  case_opened_date?: string;
  total_members: number;
  open_needs_count: number;
  active_services_count: number;
  upcoming_follow_ups?: Array<{
    id: number;
    next_contact_date: string;
    next_contact_time?: string;
    contact_method?: string;
    pathways_identified?: string[];
  }>;
  other_members?: Array<{
    member_id: number;
    first_name: string;
    last_name: string;
    role_in_household: string;
  }> | null;
}

const ROLE_LABELS: Record<string, string> = {
  HEAD_OF_HOUSEHOLD: 'Head of Household',
  SPOUSE: 'Spouse',
  CHILD: 'Child',
  DEPENDENT: 'Dependent',
  OTHER: 'Other',
};

const ROLE_COLORS: Record<string, string> = {
  HEAD_OF_HOUSEHOLD: 'bg-indigo-100 text-indigo-800',
  SPOUSE: 'bg-purple-100 text-purple-800',
  CHILD: 'bg-blue-100 text-blue-800',
  DEPENDENT: 'bg-teal-100 text-teal-800',
  OTHER: 'bg-gray-100 text-gray-800',
};

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  CLOSED: 'bg-gray-100 text-gray-800',
  ARCHIVED: 'bg-yellow-100 text-yellow-800',
};

const DISPLACEMENT_COLORS: Record<string, string> = {
  DISPLACED: 'bg-red-100 text-red-800',
  TEMPORARY_HOUSING: 'bg-orange-100 text-orange-800',
  PERMANENT_REHOUSED: 'bg-blue-100 text-blue-800',
  RETURNED: 'bg-green-100 text-green-800',
};

interface AvailableHousehold {
  id: number;
  household_number: string;
  address_line1?: string;
  city?: string;
  state?: string;
  disaster_event?: string;
  status: string;
  member_count?: number;
}

interface Props {
  memberId: number;
  onNavigate?: (tab: string, householdId?: number) => void;
}

export default function MemberHouseholdsTab({ memberId, onNavigate }: Props) {
  const [households, setHouseholds] = useState<MemberHousehold[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);

  const loadHouseholds = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/api/members/${memberId}/households`);
      setHouseholds(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      console.error('Failed to load member households:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load households');
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    loadHouseholds();
  }, [loadHouseholds]);

  const openHousehold = (householdId: number) => {
    // Primary: use parent onNavigate prop
    if (onNavigate) {
      onNavigate('household-view', householdId);
      return;
    }
    // Fallback: dispatch global event for app.tsx to handle
    window.dispatchEvent(
      new CustomEvent('open-household', { detail: { householdId } })
    );
  };

  const handleAdded = (newHouseholdId?: number) => {
    setShowAddDialog(false);
    if (newHouseholdId) {
      openHousehold(newHouseholdId);
    } else {
      loadHouseholds();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-indigo-600" />
        <span className="ml-2 text-sm text-gray-500">Loading households...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
        <p className="text-sm text-red-600">{error}</p>
        <button
          onClick={loadHouseholds}
          className="mt-3 px-3 py-1.5 text-sm border border-red-300 rounded-md hover:bg-red-100"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Households</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Households this member belongs to
          </p>
        </div>
        <button
          onClick={() => setShowAddDialog(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-v2accent rounded-md hover:bg-v2accent/90"
        >
          <Plus className="w-4 h-4" />
          Add to Household
        </button>
      </div>

      {/* Empty state */}
      {households.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white py-12 text-center">
          <Home className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-base font-medium text-gray-900">Not part of any household</h3>
          <p className="text-sm text-gray-500 mt-1 mb-4">
            This member is not currently associated with any household.
          </p>
          <button
            onClick={() => setShowAddDialog(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-v2accent rounded-md hover:bg-v2accent/90"
          >
            <Plus className="w-4 h-4" />
            Add to Household
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {households.map((hh) => {
            const address = [hh.address_line1, hh.city, hh.state, hh.zip_code].filter(Boolean).join(', ');
            const currentAddress = [
              hh.current_address_line1,
              hh.current_city,
              hh.current_state,
              hh.current_zip,
            ]
              .filter(Boolean)
              .join(', ');

            return (
              <div key={hh.household_member_id} className="rounded-lg border border-gray-200 bg-white hover:shadow-md transition-shadow">
                <div className="p-5">
                  {/* Header row */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => openHousehold(hh.household_id)}
                          className="font-mono text-sm font-semibold text-indigo-600 hover:text-indigo-700 hover:underline cursor-pointer"
                        >
                          {hh.household_number}
                        </button>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_COLORS[hh.role_in_household] || ROLE_COLORS.OTHER}`}>
                          {ROLE_LABELS[hh.role_in_household] || hh.role_in_household}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[hh.household_status] || STATUS_COLORS.ACTIVE}`}>
                          {hh.household_status}
                        </span>
                        {hh.displacement_status && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${DISPLACEMENT_COLORS[hh.displacement_status] || 'bg-gray-100 text-gray-800'}`}>
                            {hh.displacement_status.replace(/_/g, ' ')}
                          </span>
                        )}
                        {hh.is_displaced && (
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-red-50 text-red-700 inline-flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Displaced
                          </span>
                        )}
                      </div>
                      {hh.disaster_event && (
                        <p className="text-sm text-gray-700 mt-1">{hh.disaster_event}</p>
                      )}
                    </div>
                    <button
                      onClick={() => openHousehold(hh.household_id)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                    >
                      Open
                      <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* Pre-disaster address */}
                  {address && (
                    <div className="flex items-start gap-2 text-sm text-gray-600 mt-2">
                      <MapPin className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-xs text-gray-400">Pre-disaster address</div>
                        <div>{address}</div>
                      </div>
                    </div>
                  )}

                  {/* Current contact (if displaced) */}
                  {(currentAddress || hh.current_phone || hh.current_email) && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <div className="text-xs font-medium text-gray-500 mb-1">Current contact</div>
                      <div className="space-y-1 text-sm">
                        {currentAddress && (
                          <div className="flex items-center gap-1 text-gray-700">
                            <MapPin className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
                            {currentAddress}
                          </div>
                        )}
                        <div className="flex items-center gap-4 text-gray-600">
                          {hh.current_phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {hh.current_phone}
                            </span>
                          )}
                          {hh.current_email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {hh.current_email}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Relationship line */}
                  {hh.relationship_to_head && (
                    <p className="text-xs text-gray-500 mt-2">
                      Relationship to head: {hh.relationship_to_head}
                    </p>
                  )}

                  {/* Stats row */}
                  <div className="grid grid-cols-4 gap-3 mt-4 pt-3 border-t border-gray-100">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-xs text-gray-500 mb-0.5">
                        <Users className="w-3 h-3" />
                        Members
                      </div>
                      <div className="text-lg font-semibold">{hh.total_members}</div>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-xs text-gray-500 mb-0.5">
                        <AlertTriangle className="w-3 h-3" />
                        Open Needs
                      </div>
                      <div
                        className={`text-lg font-semibold ${
                          hh.open_needs_count > 0 ? 'text-orange-600' : 'text-gray-900'
                        }`}
                      >
                        {hh.open_needs_count}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-xs text-gray-500 mb-0.5">
                        <ClipboardList className="w-3 h-3" />
                        Active Services
                      </div>
                      <div
                        className={`text-lg font-semibold ${
                          hh.active_services_count > 0 ? 'text-blue-600' : 'text-gray-900'
                        }`}
                      >
                        {hh.active_services_count}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-xs text-gray-500 mb-0.5">
                        <Calendar className="w-3 h-3" />
                        Case Opened
                      </div>
                      <div className="text-xs font-semibold text-gray-900 pt-1">
                        {hh.case_opened_date
                          ? new Date(hh.case_opened_date).toLocaleDateString()
                          : '—'}
                      </div>
                    </div>
                  </div>

                  {/* Other household members */}
                  {hh.other_members && hh.other_members.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <div className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        Other Household Members
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {hh.other_members.map((om) => (
                          <button
                            key={om.member_id}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 hover:bg-indigo-100 hover:text-indigo-700 transition-colors"
                            onClick={() =>
                              window.dispatchEvent(
                                new CustomEvent('open-member', {
                                  detail: { memberId: om.member_id },
                                })
                              )
                            }
                          >
                            {om.first_name} {om.last_name}
                            <span className="text-gray-400 font-normal">
                              ({ROLE_LABELS[om.role_in_household] || om.role_in_household})
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Upcoming follow-ups */}
                  {hh.upcoming_follow_ups && hh.upcoming_follow_ups.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <div className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Upcoming Follow-ups
                      </div>
                      <div className="space-y-1.5">
                        {hh.upcoming_follow_ups.slice(0, 3).map((fu) => (
                          <div
                            key={fu.id}
                            className="flex items-center gap-2 text-sm bg-blue-50 rounded px-2 py-1.5"
                          >
                            <Calendar className="w-3.5 h-3.5 text-blue-600" />
                            <span className="font-medium">
                              {new Date(fu.next_contact_date).toLocaleDateString()}
                              {fu.next_contact_time && ` at ${fu.next_contact_time}`}
                            </span>
                            {fu.contact_method && (
                              <span className="text-xs px-2 py-0.5 rounded-full border border-gray-300 text-gray-700">
                                {fu.contact_method.replace(/_/g, ' ')}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add to Household Dialog */}
      {showAddDialog && (
        <AddToHouseholdDialog
          memberId={memberId}
          existingHouseholdIds={households.map((h) => h.household_id)}
          onClose={() => setShowAddDialog(false)}
          onAdded={handleAdded}
        />
      )}
    </div>
  );
}

// ============================================================================
// Add to Household Dialog
// ============================================================================

interface AddDialogProps {
  memberId: number;
  existingHouseholdIds: number[];
  onClose: () => void;
  onAdded: (newHouseholdId?: number) => void;
}

function AddToHouseholdDialog({ memberId, existingHouseholdIds, onClose, onAdded }: AddDialogProps) {
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [availableHouseholds, setAvailableHouseholds] = useState<AvailableHousehold[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedHouseholdId, setSelectedHouseholdId] = useState<number | null>(null);
  const [role, setRole] = useState('OTHER');
  const [relationshipToHead, setRelationshipToHead] = useState('');
  const [isDisplaced, setIsDisplaced] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  // New household form fields
  const [newHh, setNewHh] = useState({
    address_line1: '',
    city: '',
    state: '',
    zip_code: '',
    county: '',
    property_type: '',
    had_insurance: false,
    disaster_event: '',
    displacement_status: 'DISPLACED',
  });
  // Configured (custom) household fields — {field_name: value}. See TRUS-379.
  const [customFields, setCustomFields] = useState<Record<string, any>>({});

  // Load available households when in 'existing' mode
  useEffect(() => {
    if (mode !== 'existing') return;
    const loadList = async () => {
      setLoadingList(true);
      try {
        const params: Record<string, string> = { status: 'ACTIVE' };
        if (search) params.search = search;
        const response = await apiClient.get('/api/households', { params });
        // Show all results — ones the member is already in are shown as disabled
        const all = Array.isArray(response.data) ? response.data : [];
        setAvailableHouseholds(all);
      } catch (err: any) {
        console.error('Failed to load households:', err);
        setDialogError(
          err.response?.data?.detail || err.message || 'Failed to load households'
        );
      } finally {
        setLoadingList(false);
      }
    };
    const debounce = setTimeout(loadList, search ? 300 : 0);
    return () => clearTimeout(debounce);
  }, [mode, search]);

  const handleAddToExisting = async () => {
    if (!selectedHouseholdId) return;
    setSaving(true);
    setDialogError(null);
    try {
      await apiClient.post(`/api/households/${selectedHouseholdId}/members`, {
        member_id: memberId,
        role_in_household: role,
        relationship_to_head: relationshipToHead || undefined,
        is_displaced: isDisplaced,
      });
      onAdded(selectedHouseholdId);
    } catch (err: any) {
      setDialogError(err.response?.data?.detail || err.message || 'Failed to add to household');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAndAdd = async () => {
    if (!newHh.address_line1 && !newHh.disaster_event) {
      setDialogError('Please provide at least an address or disaster event');
      return;
    }
    setSaving(true);
    setDialogError(null);
    try {
      const cboId = parseInt(localStorage.getItem('active_cbo_id') || '0');
      // Create household
      const createResp = await apiClient.post('/api/households', {
        cbo_id: cboId,
        address_line1: newHh.address_line1 || undefined,
        city: newHh.city || undefined,
        state: newHh.state || undefined,
        zip_code: newHh.zip_code || undefined,
        county: newHh.county || undefined,
        property_type: newHh.property_type || undefined,
        had_insurance: newHh.had_insurance,
        disaster_event: newHh.disaster_event || undefined,
        displacement_status: newHh.displacement_status,
        custom_fields: Object.keys(customFields).length ? customFields : undefined,
      });
      const newId = createResp.data.id;
      // Add the member
      await apiClient.post(`/api/households/${newId}/members`, {
        member_id: memberId,
        role_in_household: role,
        relationship_to_head: relationshipToHead || undefined,
        is_displaced: isDisplaced,
      });
      onAdded(newId);
    } catch (err: any) {
      setDialogError(err.response?.data?.detail || err.message || 'Failed to create household');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h3 className="text-lg font-semibold">Add Member to Household</h3>
            <p className="text-sm text-gray-500">Associate this member with a household</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Mode tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setMode('existing')}
            className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 ${
              mode === 'existing'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Add to Existing
          </button>
          <button
            onClick={() => setMode('new')}
            className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 ${
              mode === 'new'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Create New Household
          </button>
        </div>

        <div className="p-5 space-y-4">
          {dialogError && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
              {dialogError}
            </div>
          )}

          {mode === 'existing' ? (
            <>
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search Households
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by household #, address, disaster event..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
              </div>

              {/* List */}
              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md">
                {loadingList ? (
                  <div className="p-6 text-center">
                    <Loader2 className="w-5 h-5 animate-spin text-indigo-600 mx-auto" />
                  </div>
                ) : availableHouseholds.length === 0 ? (
                  <div className="p-6 text-center text-sm text-gray-500">
                    {search ? 'No households match your search' : 'No available active households'}
                  </div>
                ) : (
                  <div className="divide-y">
                    {availableHouseholds.map((hh) => {
                      const address = [hh.address_line1, hh.city, hh.state].filter(Boolean).join(', ');
                      const alreadyMember = existingHouseholdIds.includes(hh.id);
                      return (
                        <button
                          key={hh.id}
                          onClick={() => !alreadyMember && setSelectedHouseholdId(hh.id)}
                          disabled={alreadyMember}
                          className={`w-full text-left p-3 ${
                            alreadyMember
                              ? 'bg-gray-50 opacity-60 cursor-not-allowed'
                              : selectedHouseholdId === hh.id
                              ? 'bg-indigo-50'
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-sm font-semibold text-indigo-600">
                                  {hh.household_number}
                                </span>
                                {hh.disaster_event && (
                                  <span className="text-xs text-gray-500">{hh.disaster_event}</span>
                                )}
                                {alreadyMember && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">
                                    Already a member
                                  </span>
                                )}
                              </div>
                              {address && (
                                <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {address}
                                </div>
                              )}
                            </div>
                            {selectedHouseholdId === hh.id && !alreadyMember && (
                              <div className="w-5 h-5 bg-v2accent rounded-full flex items-center justify-center">
                                <span className="text-white text-xs">✓</span>
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* New Household Form */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-900">Household Details</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Disaster Event
                    </label>
                    <input
                      type="text"
                      value={newHh.disaster_event}
                      onChange={(e) => setNewHh({ ...newHh, disaster_event: e.target.value })}
                      placeholder="e.g. Maui Wildfires 2023"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Pre-Disaster Address
                    </label>
                    <input
                      type="text"
                      value={newHh.address_line1}
                      onChange={(e) => setNewHh({ ...newHh, address_line1: e.target.value })}
                      placeholder="100 Main St"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={newHh.city}
                      onChange={(e) => setNewHh({ ...newHh, city: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">County</label>
                    <input
                      type="text"
                      value={newHh.county}
                      onChange={(e) => setNewHh({ ...newHh, county: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">State</label>
                    <input
                      type="text"
                      value={newHh.state}
                      onChange={(e) => setNewHh({ ...newHh, state: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Zip Code</label>
                    <input
                      type="text"
                      value={newHh.zip_code}
                      onChange={(e) => setNewHh({ ...newHh, zip_code: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Property Type
                    </label>
                    <select
                      value={newHh.property_type}
                      onChange={(e) => setNewHh({ ...newHh, property_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="">Select...</option>
                      <option value="OWNED">Owned</option>
                      <option value="RENTED">Rented</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Displacement Status
                    </label>
                    <select
                      value={newHh.displacement_status}
                      onChange={(e) => setNewHh({ ...newHh, displacement_status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="DISPLACED">Displaced</option>
                      <option value="TEMPORARY_HOUSING">Temporary Housing</option>
                      <option value="PERMANENT_REHOUSED">Permanently Rehoused</option>
                      <option value="RETURNED">Returned</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Configured household fields (Applies to → Households). TRUS-379 */}
              <HouseholdCustomFields
                values={customFields}
                onChange={(name, value) =>
                  setCustomFields((prev) => ({ ...prev, [name]: value }))
                }
              />
            </>
          )}

          {/* Member's role in the household (shared between modes) */}
          <div className="border-t pt-4 space-y-3">
            <h4 className="text-sm font-medium text-gray-900">This Member's Role</h4>
            <MembershipRoleFields
              role={role}
              onRoleChange={setRole}
              relationshipToHead={relationshipToHead}
              onRelationshipChange={setRelationshipToHead}
              isDisplaced={isDisplaced}
              onDisplacedChange={setIsDisplaced}
            />
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end gap-2 p-5 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-md hover:bg-white"
          >
            Cancel
          </button>
          {mode === 'existing' ? (
            <button
              onClick={handleAddToExisting}
              disabled={!selectedHouseholdId || saving}
              className="px-4 py-2 text-sm font-medium text-white bg-v2accent rounded-md hover:bg-v2accent/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Adding...' : 'Add to Household'}
            </button>
          ) : (
            <button
              onClick={handleCreateAndAdd}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-v2accent rounded-md hover:bg-v2accent/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Creating...' : 'Create & Add Member'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
